// EsimDetailsPageV2.js
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ChevronLeft, MoreVertical, Download, ChevronRight, ChevronDown,
    AlertTriangle, Edit3, Share2, Archive, Plus, Clock, CalendarDays, Database,
    BarChart3, History, Wallet, Link2, RotateCcw,
} from 'lucide-react';
import './EsimDetailsPageV2.css';
import { WA_CS_LINK } from '../config/whatsapp';
import useSimRefund from '../refund/useSimRefund';
import { INELIGIBLE_LABELS, customerStatus } from '../refund/refundApi';
import { useLanguage } from '../i18n/LanguageContext';
import RenameEsimModal from './component/RenameEsimModal';
import ShareEsimModal from './component/ShareEsimModal';
import SimReviewCard from './component/SimReviewCard';
import SimInstallCard from './component/SimInstallCard';
import ArchiveEsimModal from './component/ArchiveEsimModal';

const FLAG_OVERRIDES = { eur: 'eu', eu: 'eu', uk: 'gb' };
const flagUrl = (code) => {
    if (!code) return null;
    const c = String(code).toLowerCase();
    const mapped = c in FLAG_OVERRIDES ? FLAG_OVERRIDES[c] : (c.length === 2 ? c : null);
    return mapped ? `https://flagcdn.com/w80/${mapped}.png` : null;
};

// navigator.clipboard is the modern path but it rejects whenever the document
// isn't focused, so keep the execCommand fallback for those cases. Returns
// whether the text made it to the clipboard.
const copyToClipboard = (text) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    // Keep it off-screen but still selectable — display:none would not be.
    ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
    document.body.appendChild(ta);
    try {
        ta.select();
        ta.setSelectionRange(0, text.length);
        return document.execCommand('copy');
    } catch (e) {
        console.error('Clipboard copy failed', e);
        return false;
    } finally {
        document.body.removeChild(ta);
    }
};

// `key` is the label under esimDetail.planStatus.
const PLAN_STATUS = {
    ACTIVE: { key: 'active', cls: 'active' },
    TERMINATED: { key: 'terminated', cls: 'terminated' },
    NOT_ACTIVE: { key: 'notActive', cls: 'pending' },
};
const planStatus = (s) => PLAN_STATUS[String(s || '').toUpperCase()] || { key: 'active', cls: 'active' };

// Labels live in esimDetail.sorts.<key>.
const SORTS = {
    'price-asc': (a, b) => a.price - b.price,
    'price-desc': (a, b) => b.price - a.price,
    'data-asc': (a, b) => a.gb - b.gb,
    'data-desc': (a, b) => b.gb - a.gb,
};

const formatIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const formatGB = (gb) => `${Number(gb || 0).toFixed(Number.isInteger(Number(gb)) ? 0 : 1)} GB`;
const gbLabel = (gb) => `${Number(gb || 0).toFixed(Number.isInteger(Number(gb)) ? 0 : 1)}GB`;
// Every expiry on this page renders in the device's own timezone, so the two
// tabs never disagree about when a SIM runs out. The named zone stays in the
// output so a traveller reading this abroad can tell which clock it refers to.
// Each formatter takes the UI language's locale.
const formatLocalDateTime = (dt, locale) => dt.toLocaleString(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
});
// Plan expiries arrive as bare UTC timestamps ("2025-07-28 06:01:00").
const formatExpiry = (endTime, locale) => {
    if (!endTime || endTime === '0000-00-00 00:00:00') return '';
    const dt = new Date(endTime.replace(' ', 'T') + 'Z');
    if (Number.isNaN(dt.getTime())) return '';
    return formatLocalDateTime(dt, locale);
};
// The usage dates are ISO-ish but not uniform: expiry carries a compact offset
// ("2026-09-12T22:33:11+0000") that Safari rejects without a colon, while
// useBefore is plain UTC ("2026-11-12T09:40:55.802Z"). One parser covers both.
const parseUsageDate = (value) => {
    if (!value) return null;
    const dt = new Date(String(value).replace(/([+-]\d{2})(\d{2})$/, '$1:$2'));
    return Number.isNaN(dt.getTime()) ? null : dt;
};
const formatUsageExpiry = (value, locale) => {
    const dt = parseUsageDate(value);
    return dt ? formatLocalDateTime(dt, locale) : '';
};
// useBefore is an install deadline, not a moment — the hour would be noise.
const formatUseBefore = (value, locale) => {
    const dt = parseUsageDate(value);
    return dt ? dt.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
};

// 270° SVG ring gauge.
const Gauge = ({ remaining, total }) => {
    const R = 52;
    const C = 2 * Math.PI * R;
    const ARC = C * 0.75; // 270°
    const pct = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 0;
    return (
        <svg className="ed-gauge-svg" viewBox="0 0 120 120">
            <g transform="rotate(135 60 60)">
                <circle cx="60" cy="60" r={R} fill="none" stroke="#e8f6ed" strokeWidth="11"
                    strokeLinecap="round" strokeDasharray={`${ARC} ${C}`} />
                <circle cx="60" cy="60" r={R} fill="none" stroke="#237804" strokeWidth="11"
                    strokeLinecap="round" strokeDasharray={`${ARC * pct} ${C}`} />
            </g>
        </svg>
    );
};

const EsimDetailsPageV2 = () => {
    const { SimId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t, locale } = useLanguage();

    const [data, setData] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [tab, setTab] = useState('kuota'); // 'kuota' | 'riwayat'
    const [menuOpen, setMenuOpen] = useState(false);
    const [showRename, setShowRename] = useState(false);
    const [showShareEmail, setShowShareEmail] = useState(false);
    const [showArchive, setShowArchive] = useState(false);
    // A dictionary key, so the toast follows the current language.
    const [shareToast, setShareToast] = useState('');

    const [topups, setTopups] = useState([]);
    const [sortKey, setSortKey] = useState('price-asc');
    const [sortOpen, setSortOpen] = useState(false);

    // { SERVICE?, INSTALL? } — fetched once and handed to both feedback cards,
    // so neither asks a question this customer has already answered.
    const [reviews, setReviews] = useState(null);

    const menuRef = useRef(null);
    const sortRef = useRef(null);

    const refundState = useSimRefund(SimId);

    useEffect(() => {
        const fetchEsimData = async () => {
            try {
                const res = await fetch(`https://api.artamobile.id/v1/esims/id/${SimId}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                setData(await res.json());
            } catch (error) {
                console.error('Failed to fetch eSIM data:', error);
                setLoadError(true);
            }
        };
        fetchEsimData();
    }, [SimId]);

    useEffect(() => {
        let cancelled = false;

        const fetchReviews = async () => {
            try {
                const res = await fetch(`https://api.artamobile.id/v2/sim/${SimId}/review`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);

                const body = await res.json();
                if (!cancelled) setReviews(body?.reviews || {});
            } catch (error) {
                // A failed lookup just means the cards show their empty state.
                if (!cancelled) setReviews({});
            }
        };
        fetchReviews();

        return () => { cancelled = true; };
    }, [SimId]);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const res = await fetch(`https://api.artamobile.id/v1/product/topup/${SimId}`, { signal: ctrl.signal });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const json = await res.json();
                const list = (json?.productList || []).map((p) => ({
                    ...p,
                    validity: parseInt(p.validity, 10),
                    price: Number(p.price) || 0,
                    gb: typeof p.gb === 'number' ? p.gb : parseInt(String(p.balance).replace(/\D/g, ''), 10),
                }));
                if (!ctrl.signal.aborted) setTopups(list);
            } catch (e) {
                if (e.name !== 'AbortError') console.error('Failed to fetch topups', e);
            }
        })();
        return () => ctrl.abort();
    }, [SimId]);

    // Close kebab / sort menus on outside click.
    useEffect(() => {
        const onDocClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
            if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    // Auto-dismiss the "link copied" confirmation.
    useEffect(() => {
        if (!shareToast) return;
        const t = setTimeout(() => setShareToast(''), 2500);
        return () => clearTimeout(t);
    }, [shareToast]);

    if (loadError) {
        return <div className="ed-page"><div className="ed-state">{t('esimDetail.loadError')}</div></div>;
    }
    if (!data) {
        return (
            <div className="ed-page">
                <div className="ed-skel ed-skel-hero" />
                <div className="ed-body">
                    <div className="ed-skel ed-skel-block" />
                    <div className="ed-skel ed-skel-block" />
                </div>
            </div>
        );
    }

    const { esim, region, plans = [], usage = {} } = data;
    const total = Math.max(0, usage?.quota ?? 0);
    const remaining = Math.max(0, Math.min(total, usage?.balance ?? 0));
    const used = Math.max(0, total - remaining);
    // An eSIM that hasn't been activated yet reports useBefore (the deadline to
    // start it); once it is running the API sends expiry instead. Only one is
    // ever present, so they share a single row.
    const usageExpiry = formatUsageExpiry(usage?.expiry, locale);
    const useBefore = formatUseBefore(usage?.useBefore, locale);
    const expiryRow = usageExpiry ? { label: t('esimDetail.validUntil'), value: usageExpiry }
        : useBefore ? { label: t('esimDetail.useBefore'), value: useBefore }
        : null;
    // simType 0 is a physical SIM card; anything else (or a missing value) is an
    // eSIM. Keep this in step with EsimCard so a SIM reads the same in the list
    // and on its detail page. A physical SIM has nothing to install and can't be
    // "deleted", so the install guide and the deletion warning don't apply.
    const isPhysicalSim = parseInt(esim.simType, 10) === 0;
    const label = esim.label || `${isPhysicalSim ? "SIM" : "eSIM"} ${region.name}`;
    const flag = flagUrl(region.regionCode);
    const activePlan = plans.find((p) => String(p.status).toUpperCase() === 'ACTIVE') || plans[0];
    const sortedTopups = topups.slice().sort(SORTS[sortKey]);

    // Share this page's link. Uses the native share sheet where the browser has
    // one (all iOS/Android browsers), and falls back to copying the URL on
    // desktop, where navigator.share mostly doesn't exist.
    const shareLink = async () => {
        const url = window.location.href;
        const payload = { title: t('esimDetail.shareTitle', { label }), text: t('esimDetail.shareText', { label }), url };

        if (navigator.share) {
            try {
                await navigator.share(payload);
                return;
            } catch (e) {
                // The user dismissing the sheet is not an error worth reporting.
                if (e?.name === 'AbortError') return;
                console.error('Share failed, falling back to clipboard', e);
            }
        }

        let copied = false;
        try {
            await navigator.clipboard.writeText(url);
            copied = true;
        } catch (e) {
            // Rejects when the document isn't focused, or on an insecure origin.
            copied = copyToClipboard(url);
        }
        setShareToast(copied ? 'esimDetail.linkCopied' : 'esimDetail.linkCopyFailed');
    };

    // Refund entry point. Not shown for a physical SIM, nor to someone the eSIM
    // was shared with: only the buyer can ask for their money back.
    const refund = refundState.refund;
    const refundView = refund ? customerStatus(refund.status) : null;
    const refundOpen = refundView && !['done', 'rejected'].includes(refundView.key);
    const ineligibleKey = refundState.eligibility && !refundState.eligibility.eligible
        ? INELIGIBLE_LABELS[refundState.eligibility.reason]
        : undefined;
    const requestLabel = t('esimDetail.requestRefund');
    const refundMenu = (() => {
        if (isPhysicalSim) return null;
        if (refund) return { to: `/sim/${SimId}/refund/status`, label: t('esimDetail.refundStatus') };
        if (refundState.status === 'anonymous') return { to: `/sim/${SimId}/refund`, label: requestLabel };
        if (refundState.status === 'loading') return { disabled: true, label: requestLabel, sub: t('esimDetail.refundChecking') };
        if (refundState.status !== 'ready') return null;
        if (refundState.eligibility?.eligible) return { to: `/sim/${SimId}/refund`, label: requestLabel };
        return ineligibleKey ? { disabled: true, label: requestLabel, sub: t(ineligibleKey) } : null;
    })();

    const goTopUp = (code) => {
        const params = new URLSearchParams(location.search);
        params.set('topup', '1');
        params.set('iccid', esim.iccid);
        params.set('SimId', esim.SimId);
        navigate(`/product/${code}?${params.toString()}`);
    };

    return (
        <div className="ed-page">
            {/* Hero — same treatment as RegionPage (white-fade + flat top bar) */}
            <div className="ed-hero">
                {region.shopImageUrl && <img className="ed-hero-img" src={region.shopImageUrl} alt="" />}
                <div className="ed-hero-fade" />
                <div className="ed-topbar">
                    <button className="ed-icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}><ChevronLeft size={22} /></button>
                    <span className="ed-topbar-title">{label}</span>
                    <div className="ed-menu" ref={menuRef}>
                        <button className="ed-icon-btn" onClick={() => setMenuOpen((o) => !o)} aria-label={t('common.menu')}><MoreVertical size={22} /></button>
                        {menuOpen && (
                            <div className="ed-menu-pop">
                                <button onClick={() => { setMenuOpen(false); setShowRename(true); }}><Edit3 size={16} /> {t('esimDetail.rename')}</button>
                                <button onClick={() => { setMenuOpen(false); shareLink(); }}><Link2 size={16} /> {t('esimDetail.share')}</button>
                                <button onClick={() => { setMenuOpen(false); setShowShareEmail(true); }}><Share2 size={16} /> {t('esimDetail.sendEmail')}</button>
                                <div className="ed-menu-divider" role="separator" />
                                {refundMenu && (refundMenu.disabled ? (
                                    <button disabled aria-disabled="true" className="ed-menu-disabled">
                                        <RotateCcw size={16} />
                                        <span className="ed-menu-stack">{refundMenu.label}<small>{refundMenu.sub}</small></span>
                                    </button>
                                ) : (
                                    <button onClick={() => { setMenuOpen(false); navigate(refundMenu.to); }}>
                                        {refund ? <Clock size={16} /> : <RotateCcw size={16} />} {refundMenu.label}
                                    </button>
                                ))}
                                {refundOpen ? (
                                    // Archiving would hide the SIM, and with it the refund
                                    // banner, while CS is still working on it.
                                    <button disabled aria-disabled="true" className="ed-menu-disabled">
                                        <Archive size={16} />
                                        <span className="ed-menu-stack">{t('esimDetail.archive')}<small>{t('esimDetail.archiveWait')}</small></span>
                                    </button>
                                ) : (
                                    <button onClick={() => { setMenuOpen(false); setShowArchive(true); }}><Archive size={16} /> {t('esimDetail.archive')}</button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className="ed-hero-info">
                    {flag && <img className="ed-hero-flag" src={flag} alt="" />}
                    <h1 className="ed-hero-name">{region.name}</h1>
                    <span className="ed-hero-iccid">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" stroke="#595959" strokeWidth="2" strokeLinejoin="round" />
                            <rect x="9" y="12" width="6" height="6" rx="1" stroke="#595959" strokeWidth="2" />
                        </svg>
                        ICCID: {esim.iccid}
                    </span>
                </div>
            </div>

            <div className="ed-body">
                {refund && (
                    <button
                        className={`ed-refund-banner ${refundView.tone}`}
                        onClick={() => navigate(`/sim/${SimId}/refund/status`)}
                    >
                        <Clock size={20} className="ed-refund-banner-icon" />
                        <span className="ed-refund-banner-text">
                            <strong>{t(`esimDetail.refundBanner.${refundView.key}.title`)}</strong>
                            <span>{t(`esimDetail.refundBanner.${refundView.key}.sub`, { sla: t('refund.slaLabel') })}</span>
                        </span>
                        <ChevronRight size={20} />
                    </button>
                )}

                {/* Install card — eSIM only; a physical SIM is already in the device */}
                {!isPhysicalSim && (
                    <button
                        className="ed-install"
                        onClick={() => navigate('/esims/install', { state: { qrCodeUrl: `https://artaqrcode.s3.ap-southeast-1.amazonaws.com/${esim.filename}`, activationCode: esim.activationCode } })}
                    >
                        <span className="ed-install-icon"><Download size={20} /></span>
                        <span className="ed-install-text">
                            <strong>{t('esimDetail.installTitle')}</strong>
                            <span>{t('esimDetail.installSub')}</span>
                        </span>
                        <span className="ed-install-chev"><ChevronRight size={20} /></span>
                    </button>
                )}

                {/* Tabs */}
                <div className="ed-tabs" role="tablist">
                    <button className={`ed-tab ${tab === 'kuota' ? 'active' : ''}`} onClick={() => setTab('kuota')}><BarChart3 size={16} /> {t('esimDetail.tabQuota')}</button>
                    <button className={`ed-tab ${tab === 'riwayat' ? 'active' : ''}`} onClick={() => setTab('riwayat')}><History size={16} /> {t('esimDetail.tabHistory')}</button>
                </div>

                {tab === 'kuota' ? (
                    <div className="ed-card ed-usage">
                        <div className="ed-usage-head">
                            <h2 className="ed-usage-title">{region.name} {gbLabel(total)}</h2>
                            <div className="ed-usage-chips">
                                {activePlan?.validity != null && (
                                    <span className="ed-chip"><Clock size={12} /> {t('common.days', { n: activePlan.validity })}</span>
                                )}
                            </div>
                        </div>
                        <div className="ed-gauge">
                            <Gauge remaining={remaining} total={total} />
                            <div className="ed-gauge-center">
                                <span className="ed-gauge-value">{formatGB(remaining)}</span>
                                <span className="ed-gauge-label">{t('esimDetail.gbLeft')}</span>
                            </div>
                        </div>
                        <div className="ed-usage-stats">
                            <div><strong>{formatGB(used)}</strong><span className="ed-stat-label">{t('esimDetail.usedData')}</span></div>
                            <div><strong>{formatGB(total)}</strong><span className="ed-stat-label">{t('esimDetail.totalData')}</span></div>
                        </div>
                        {expiryRow && (
                            <div className="ed-usage-expiry">
                                <span className="ed-usage-expiry-label"><CalendarDays size={16} /> {expiryRow.label}</span>
                                <strong>{expiryRow.value}</strong>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="ed-history">
                        {plans.length === 0 ? (
                            <div className="ed-state">{t('esimDetail.noHistory')}</div>
                        ) : plans.map((pkg, idx) => {
                            const st = pkg.status ? planStatus(pkg.status) : null;
                            return (
                                <div className="ed-card ed-plan" key={idx}>
                                    <div className="ed-plan-head">
                                        <span className="ed-plan-title">{formatGB(pkg.quota)}</span>
                                        {st && <span className={`ed-plan-status ${st.cls}`}>{t(`esimDetail.planStatus.${st.key}`)}</span>}
                                    </div>
                                    <div className="ed-plan-rows">
                                        <div className="ed-plan-row"><span><CalendarDays size={16} /> {t('esimDetail.validity')}</span><strong>{t('common.days', { n: pkg.validity || 0 })}</strong></div>
                                        <div className="ed-plan-row"><span><Database size={16} /> {t('esimDetail.quota')}</span><strong>{formatGB(pkg.quota)}</strong></div>
                                        {formatExpiry(pkg.expiry, locale) && (
                                            <div className="ed-plan-row"><span><Clock size={16} /> {t('esimDetail.ends')}</span><strong>{formatExpiry(pkg.expiry, locale)}</strong></div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Warning — eSIM only; a physical SIM can't be deleted from the device */}
                {!isPhysicalSim && (
                    <div className="ed-warning">
                        <AlertTriangle size={18} className="ed-warning-icon" />
                        <div><strong>{t('esimDetail.warningTitle')}</strong> {t('esimDetail.warningBody')}</div>
                    </div>
                )}

                {/* Isi Ulang Paketmu */}
                {sortedTopups.length > 0 && (
                    <div className="ed-topup">
                        <h3 className="ed-section-title">{t('esimDetail.topupTitle')}</h3>
                        <div className="ed-topup-bar">
                            <span className="ed-topup-count">{t('common.packages', { n: sortedTopups.length })}</span>
                            <div className="ed-sort" ref={sortRef}>
                                <button className="ed-sort-btn" onClick={() => setSortOpen((o) => !o)}>
                                    {t('common.sort')} <ChevronDown size={14} />
                                </button>
                                {sortOpen && (
                                    <div className="ed-sort-menu">
                                        {Object.keys(SORTS).map((k) => (
                                            <button key={k} className={k === sortKey ? 'active' : ''}
                                                onClick={() => { setSortKey(k); setSortOpen(false); }}>{t(`esimDetail.sorts.${k}`)}</button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="ed-topup-list">
                            {sortedTopups.map((pkg) => (
                                <div className="ed-topup-row" key={pkg.code}>
                                    <div className="ed-topup-main">
                                        <span className="ed-topup-balance">{pkg.balance}</span>
                                        <div className="ed-topup-meta">
                                            <span><Wallet size={13} /> {formatIDR(pkg.price)}</span>
                                            <span><CalendarDays size={13} /> {t('common.days', { n: pkg.validity })}</span>
                                        </div>
                                    </div>
                                    <button className="ed-topup-btn" onClick={() => goTopUp(pkg.code)}>
                                        <Plus size={16} /> {t('common.topUp')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Feedback — last thing on the page, just above "Ada Kendala?".
                    Data usage decides which question is worth asking: before any
                    data is used the only useful one is whether the eSIM installed
                    at all, and smdpStatus says where in that process they are.
                    Once data is flowing they can judge the connection instead.
                    Physical SIMs have nothing to install, so they skip straight
                    to the review. */}
                {reviews && (used > 0 || isPhysicalSim ? (
                    <SimReviewCard
                        simId={esim.SimId}
                        regionName={region.name}
                        smdpStatus={esim.smdpStatus}
                        existing={reviews.SERVICE}
                    />
                ) : (
                    <SimInstallCard
                        simId={esim.SimId}
                        smdpStatus={esim.smdpStatus}
                        installationTime={esim.installationTime}
                        existing={reviews.INSTALL}
                    />
                ))}

                {/* Help */}
                <div className="ed-help">
                    <div className="ed-help-text">
                        <p className="ed-help-title">{t('help.title')}</p>
                        <p className="ed-help-desc">{t('help.desc')}</p>
                        <a className="ed-help-btn" href={WA_CS_LINK} target="_blank" rel="noopener noreferrer">{t('help.cta')}</a>
                    </div>
                    <img
                        className="ed-help-illustration"
                        src={`${process.env.PUBLIC_URL}/icons/ada-kendala-illustration.png`}
                        alt=""
                        aria-hidden="true"
                    />
                </div>
            </div>

            <RenameEsimModal
                open={showRename}
                onClose={() => setShowRename(false)}
                currentLabel={label}
                simId={esim.SimId}
                onRenamed={(newLabel) => setData((prev) => ({ ...prev, esim: { ...prev.esim, label: newLabel } }))}
            />
            <ShareEsimModal open={showShareEmail} onClose={() => setShowShareEmail(false)} simId={esim.SimId} onShared={() => {}} />
            <ArchiveEsimModal open={showArchive} onClose={() => setShowArchive(false)} simId={esim.SimId} onArchived={() => navigate('/esims')} />

            {shareToast && (
                <div className="ed-toast" role="status" aria-live="polite">{t(shareToast)}</div>
            )}
        </div>
    );
};

export default EsimDetailsPageV2;
